import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, Loader2 } from "lucide-react";
import dayjs from "dayjs";
import { companyConfig } from "./myGiftCardList";
import { listAllGiftCardRedemption } from "../../reducers/payouts.slice";
import { AuthContext } from "../../context/authContext";
import PageHeader from "../../components/shared/PageHeader";
import GiftCardTile, { retailerTitle, giftCardTypeLabel } from "../../components/member/giftcards/GiftCardTile";
import useGiftCardRedemption from "../../components/member/giftcards/useGiftCardRedemption";
import GiftCardRedemptionDialogs from "../../components/member/giftcards/GiftCardRedemptionDialogs";
import useMediaQuery from "../../components/shared/useMediaQuery";

export default function MemberMyGiftCards() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [storeCardData, setStoreCardData] = useState([]);
  const [loading, setLoading] = useState(true);

  const { selectedCard, openStoreCard, openCardProcessing, openRejectedCard, imageBase64, openCard, closeStoreCard, closeProcessing, closeRejected } =
    useGiftCardRedemption({ dispatch, showToast, companyConfig });

  useEffect(() => {
    setLoading(true);
    dispatch(listAllGiftCardRedemption()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setLoading(false);
        return;
      }
      const formattedGiftCard = res?.payload?.map((item) => ({
        ...item,
        created_at: dayjs(item?.created_at).format("MM/DD/YYYY"),
      }));
      setStoreCardData(formattedGiftCard || []);
      setLoading(false);
    });
  }, []);

  const purchasedPillFor = (card) => {
    if (card?.status === "READY_FOR_REDEMPTION") return { text: `Purchased ${card?.created_at}`, tone: "success" };
    if (card?.status === "REJECTED") return { text: "Rejected", tone: "danger" };
    return { text: "Processing", tone: "neutral" };
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 18 : 24, width: "100%", maxWidth: "100%" }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 13,
          cursor: "pointer",
          padding: 0,
          width: "fit-content",
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <PageHeader title="My Gift Cards" subtitle="Keep track of all your gift card purchases here." />

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#c3c8cc" }}>
          <Loader2 size={20} className="tw-animate-spin" style={{ marginRight: 8 }} />
          Loading…
        </div>
      ) : storeCardData?.length === 0 ? (
        <div
          style={{
            height: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed rgba(255,255,255,0.12)",
            borderRadius: 14,
            color: "#c3c8cc",
            fontSize: 13,
          }}
        >
          You haven't purchased any gift cards yet.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 180 : 210}px, 1fr))`, gap: isMobile ? 12 : 16 }}>
          {storeCardData.map((card, index) => {
            const { logo, bg } = companyConfig[card?.gift_card_details?.retailer] || { logo: "/images/amazon.png", bg: "#000000" };
            const pill = purchasedPillFor(card);
            return (
              <GiftCardTile
                key={card?.id ?? index}
                logo={logo}
                bg={bg}
                retailerName={retailerTitle(card?.gift_card_details?.retailer)}
                typeText={giftCardTypeLabel(card?.gift_card_details?.gift_card_type)}
                priceText={`$${card?.discounted_amount}`}
                pillText={pill.text}
                pillTone={pill.tone}
                onClick={() => openCard(card)}
              />
            );
          })}
        </div>
      )}

      <GiftCardRedemptionDialogs
        selectedCard={selectedCard}
        openStoreCard={openStoreCard}
        openCardProcessing={openCardProcessing}
        openRejectedCard={openRejectedCard}
        imageBase64={imageBase64}
        onCloseStoreCard={closeStoreCard}
        onCloseProcessing={closeProcessing}
        onCloseRejected={closeRejected}
      />
    </div>
  );
}
